import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from '@/hooks';
import { buildOrderLinksPayload } from '@/lib/mappers/links';
import {
  linksApi,
  useLinkShipmentOrdersMutation,
  useLinkOrderShipmentsMutation,
} from '@/lib/api/linksApi';
import { EMPTY_LIST } from '@/lib/utils';
import { useLinkableCandidates } from './useLinkableCandidates';
import { LinkOrdersModal, LINK_ORDER_FLOWS, isBulkOrderFlow } from './LinkOrdersModal';

/**
 * Props:
 *   module          route module segment, for the REST save
 *   shipments       the shipments being linked; one, or a list bulk selection
 *   flow            LINK_ORDER_FLOWS — shipmentToOrders, or the shipmentsToOrder bulk
 *   detailFields    (unitType) => the summary card's rows
 *   open            whether the wizard is shown
 *   onClose         close handler
 *   onLinked        called once the save succeeded
 */
export const LinkOrdersForShipment = ({
  module,
  shipments = EMPTY_LIST,
  flow = LINK_ORDER_FLOWS.shipmentToOrders,
  detailFields,
  open,
  onClose,
  onLinked,
}) => {
  const t = useTranslations();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null);
  const [linkShipmentOrders, { isLoading: isSavingOrders }] = useLinkShipmentOrdersMutation();
  const [linkOrderShipments, { isLoading: isSavingShipments }] = useLinkOrderShipmentsMutation();

  const isBulk = isBulkOrderFlow(flow);
  const first = shipments[0];

  const queryArgs = useMemo(
    () => shipments.map((row) => ({ shipmentId: row.id, search, sort })),
    [shipments, search, sort],
  );

  const { candidates, loading } = useLinkableCandidates(
    linksApi.endpoints.getLinkableOrders,
    queryArgs,
    open,
  );

  const handleClose = useCallback(() => {
    setSearch('');
    setSort(null);
    onClose?.();
  }, [onClose]);

  /**
   * One request either way — the direction follows whichever side ended up singular.
   * Bulk picked ONE order for the ticked shipments; otherwise one shipment takes N orders.
   */
  const handleSave = useCallback(
    async (entries, pickedOrderId) => {
      const links = buildOrderLinksPayload(entries);
      const result = isBulk
        ? await linkOrderShipments({ module, orderId: pickedOrderId, shipments: links })
        : await linkShipmentOrders({ module, shipmentId: first?.id, orders: links });
      if (result.error) return;
      onLinked?.();
      handleClose();
    },
    [isBulk, linkOrderShipments, linkShipmentOrders, module, first?.id, onLinked, handleClose],
  );

  // Closing unmounts the modal, so nothing it held survives into the next open.
  if (!open) return null;

  return (
    <LinkOrdersModal
      open={open}
      onClose={handleClose}
      title={t('LINK_ORDERS_TITLE', {
        shipmentId: shipments.map((row) => row.shipmentId).join(', '),
      })}
      detailFields={detailFields}
      candidates={candidates}
      loading={loading}
      isSaving={isSavingOrders || isSavingShipments}
      flow={flow}
      bulkRows={isBulk ? shipments : EMPTY_LIST}
      unassignedByUnit={isBulk ? undefined : first?.unassignedByUnit}
      onSearchChange={setSearch}
      onSortChange={setSort}
      onSave={handleSave}
    />
  );
};
